import { sumGa4ItemValue } from "~/hec/analytics/ga4-product-detail";
import type {
  CartUpdateAction,
  CartUpdateEventData,
} from "~/segment/cart-update";

/**
 * Multi-sink event layer — GTM dataLayer adapter for cart add/remove events.
 *
 * Maps the canonical `CartUpdateEventData` (the same server-attached payload the
 * basket action emits and the Segment `Cart Update` observer consumes —
 * `cart-update.ts`) to the GA4 `ecommerce` dataLayer event the production GTM
 * containers listen for.
 *
 * SFRA parity (the authoritative shape), with ONE deliberate divergence:
 *  - Push is `{ event, ecommerce: { currency, value, items } }` and the caller
 *    flushes `{ecommerce:null}` first — `Cart.js` GA4_addToCart /
 *    GA4_removeFromCart pushes (controllers/Cart.js:116/150/349/433).
 *
 *    DIVERGENCE (SFCC-12175 W1): SFRA emits `ecommerce: { items }` with no
 *    event-level `currency`/`value`. GA4 marks BOTH of those required on
 *    add_to_cart and remove_from_cart, and the omission is why HSA
 *    `add_to_cart` carried an event `value` on 0% of 29,294 events (BigQuery,
 *    Aug 14-18 2026). The GA4 spec wins over byte-parity here — do NOT
 *    "restore parity" by deleting these two fields in a future audit.
 *
 *    `value` is the Σ `price × quantity` item subtotal GA4 asks for, via
 *    `sumGa4ItemValue` (shared with `GA4_productDetail` so the rounding and the
 *    omit-vs-zero rule can't drift). This deliberately does NOT copy SFRA's own
 *    `view_cart` (`Cart.js:32-39`), which uses `basket.totalGrossPrice`: that is
 *    tax- and shipping-inclusive, so it is not an item-revenue figure and is the
 *    wrong convention to mirror.
 *  - Item shape mirrors `generateGA4ProductLinesDataLayer` /
 *    `generateGA4ItemsFromProductList` (dataLayerHelper.js:423/846). `affiliation`,
 *    `location_id`, `coupon`, `discount` are omitted to match the existing Workers
 *    `Ga4ProductDetailItem` convention (`ga4-product-detail.ts`).
 *  - `source` is the add-to-cart click-source and is set on the FIRST item only
 *    (`Cart.js:346/429` `gA4products[0].source = req.form.clickSource`).
 *
 * Design: docs/analytics-segment-first-event-layer.md
 */

/** GA4 `ecommerce.items[]` entry for a cart add/remove event. */
export type Ga4CartItem = {
  item_id: string;
  item_name: string | undefined;
  currency: string;
  index: number;
  item_brand: string | undefined;
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  /** SFRA hardcodes `'Shopping Cart'` on every cart-line item
   *  (`dataLayerHelper.js:487`) — the "100% item_list_name on SFRA
   *  add_to_cart" the GA4 parity audit measured. SFCC-11889 C2. */
  item_list_name: string;
  item_variant: string | null;
  price: number;
  quantity: number;
  /** Add-to-cart click-source (SFRA `req.form.clickSource`); first item only. */
  source?: string;
};

export type Ga4CartEventName =
  | "GA4_addToCart"
  | "GA4_removeFromCart"
  | "GA4_quickviewAddToCart";

export type Ga4CartEvent = {
  event: Ga4CartEventName;
  ecommerce: { currency: string; value?: number; items: Ga4CartItem[] };
};

const ACTION_TO_EVENT: Partial<
  Record<CartUpdateAction, "GA4_addToCart" | "GA4_removeFromCart">
> = {
  add: "GA4_addToCart",
  remove: "GA4_removeFromCart",
};

/**
 * GA4 event name for one basket action, or `null` when the action maps to no
 * GA4 event.
 *
 * A cart quantity edit (`modify`) maps by the SIGN of the event quantity: the
 * producer already sets it to `newQty - oldQty` (`resources/basket.tsx`
 * "update" case), and nothing between there and here normalizes it, so an
 * increase arrives positive and a decrease negative. GA4 has no "modify" event
 * — the spec models a quantity edit as `add_to_cart` / `remove_from_cart` on
 * the delta, which is exactly what the sign gives us.
 *
 * A zero delta (a no-op edit, e.g. re-submitting the same quantity) maps to no
 * event rather than a zero-quantity one.
 *
 * SFCC-12175 W5. Note the previous comment here asserted the event `quantity`
 * was an unsigned magnitude with no direction, which is why `modify` was
 * deferred — that was wrong; the signed delta has always been on the wire.
 */
const ga4EventForAction = (
  data: CartUpdateEventData,
): "GA4_addToCart" | "GA4_removeFromCart" | null => {
  if (data.action !== "modify") return ACTION_TO_EVENT[data.action] ?? null;
  if (data.quantity > 0) return "GA4_addToCart";
  if (data.quantity < 0) return "GA4_removeFromCart";
  return null;
};

/**
 * Build the single GA4 add/remove dataLayer event for one basket action from its
 * `CartUpdateEventData[]` (all affected lines). Returns `null` for an empty
 * array, or for an action with no GA4 mapping (a zero-delta `modify`).
 *
 * SFRA fires ONE event per action with every line as an item
 * (`Cart.js:344-355` builds `items` from ALL addedProducts in a single push),
 * so an addMany — e.g. the main product plus its "Complete Your Purchase"
 * cross-sells — is one `GA4_addToCart` with N items, NOT one event per line.
 *
 * `opts.quickView` upgrades an add to `GA4_quickviewAddToCart` (SFRA
 * `isQuickViewModal`). `opts.source` is the click-source, stamped on the FIRST
 * item only (`Cart.js:346/429` `gA4products[0].source`); absent on remove.
 */
export const buildGa4CartEvent = (
  events: CartUpdateEventData[],
  opts?: { source?: string; quickView?: boolean },
): Ga4CartEvent | null => {
  if (events.length === 0) return null;
  // All lines of one basket action share an action; key the event off it.
  const baseEvent = ga4EventForAction(events[0]);
  if (!baseEvent) return null;

  const event: Ga4CartEventName =
    opts?.quickView && baseEvent === "GA4_addToCart"
      ? "GA4_quickviewAddToCart"
      : baseEvent;

  const items: Ga4CartItem[] = events.map((data, i) => ({
    item_id: data.product_id,
    item_name: data.title,
    currency: data.local_currency || "USD",
    index: i + 1,
    item_brand: data.brand || undefined,
    // Full 4-level category path, server-resolved off the acted-on line's
    // `primaryCategoryId` (SFCC-11889 C2 — see `cart-update.server.ts`).
    item_category: data.category_level_1,
    item_category2: data.category_level_2,
    item_category3: data.category_level_3,
    item_category4: data.category_level_4,
    item_list_name: "Shopping Cart",
    // GA4 keeps the SFRA contract `item_variant = null` for non-variants
    // (dataLayerHelper.js:434/448-450) — the OPPOSITE of the Segment/Bloomreach
    // `Cart Update.variant` field, which CMP-58 made non-null for every type.
    // Rederive isVariant from the shared event: product_id is the master id for
    // variants (≠ sku) and the own id for non-variants (= sku).
    item_variant: data.product_id !== data.sku ? data.sku : null,
    price: data.price,
    // Magnitude: a `modify` decrease carries a negative delta (the direction
    // already consumed by `ga4EventForAction`), and GA4 expects a positive
    // item quantity on remove_from_cart. No-op for add/remove, which are
    // already positive. SFCC-12175 W5.
    quantity: Math.abs(data.quantity),
    // SFRA stamps the click-source on the first item only (Cart.js:346/429).
    ...(opts?.source && i === 0 ? { source: opts.source } : {}),
  }));

  // Every line of one basket action shares a currency, so item 0 is
  // representative; `items` is non-empty here (guarded above).
  const value = sumGa4ItemValue(items);
  return {
    event,
    ecommerce: {
      currency: items[0].currency,
      ...(value !== undefined ? { value } : {}),
      items,
    },
  };
};
